// Resume builder modal — opened from PersonPage's FloatingDock action menu.
//
// Flow:
//   Stage 1 (PASTE)  → text area, click Generate, server LLM-parses.
//                      If validation fails, missing fields render as a list
//                      ABOVE the paste box and the user adds info + retries.
//   Stage 2 (EDIT)   → split pane. Left: editable form fields for every
//                      part of the resume (name, contact, pitch, jobs,
//                      bullets, education, skills). Right: live preview of
//                      the actual template + palette, scaled to fit.
//                      Header strip lets the user pick template + palette.
//                      "Download PDF" posts the final ResumeData and
//                      streams the PDF back.
//
// Layout: 92vw × 92vh. Backdrop click outside the modal does NOT close
// (we'd lose draft state) — there's an explicit X in the corner.
//
// State management is intentionally local — no Redux, no context. The
// modal owns the draft until the user closes or downloads.

import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent as ReactDragEvent } from "react";
import { createPortal } from "react-dom";
import {
  CourierResume,
  StatementResume,
  COURIER_PALETTES,
  STATEMENT_PALETTES,
  type ResumeData,
  type ResumeJob,
  type ResumeBullet,
  type ResumeEducation,
} from "../lib/resumeTemplates";

interface MissingField {
  field: string;
  why: string;
}

interface ParseResponse {
  ok: boolean;
  data?: ResumeData;
  missing?: MissingField[];
}

interface Props {
  jobId: string;
  open: boolean;
  onClose: () => void;
  /** Optional starting paste — if you have a stored one. */
  initialPaste?: string;
}

type Stage = "paste" | "edit";
type Template = "courier" | "statement";

export function ResumeBuilderModal({ jobId, open, onClose, initialPaste = "" }: Props): JSX.Element | null {
  const [stage, setStage] = useState<Stage>("paste");
  const [paste, setPaste] = useState(initialPaste);
  const [parsing, setParsing] = useState(false);
  const [missing, setMissing] = useState<MissingField[]>([]);
  const [data, setData] = useState<ResumeData | null>(null);
  const [template, setTemplate] = useState<Template>("courier");
  const [paletteId, setPaletteId] = useState<string>("sage");
  const [downloading, setDownloading] = useState(false);

  // Reset to paste when (re)opened.
  useEffect(() => {
    if (!open) return;
    setStage("paste");
    setMissing([]);
    setData(null);
    setDownloading(false);
  }, [open]);

  // ESC closes — but only after confirming when there's unsaved
  // editing in flight. We treat any of these as "the user is mid-edit
  // and would lose work":
  //   • stage === "edit"  (they've parsed and are tweaking the resume)
  //   • paste has content but they haven't generated yet (15+ chars)
  // Closing from the bare paste screen with nothing typed is fine —
  // no draft to lose.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const hasDraft = stage === "edit" || (stage === "paste" && paste.trim().length > 15);
      if (
        hasDraft &&
        !window.confirm("Discard your draft and close the resume builder?")
      ) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, stage, paste]);

  if (!open) return null;

  const handleParse = async () => {
    setParsing(true);
    setMissing([]);
    try {
      const res = await fetch("/api/resume/parse", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ jobId, paste }),
      });
      const json = (await res.json()) as ParseResponse;
      if (json.ok && json.data) {
        setData(json.data);
        setStage("edit");
      } else if (json.missing && json.missing.length > 0) {
        setMissing(json.missing);
        // If we have a partial data shape AND the missing list isn't fatal
        // (only suggestions), let the user proceed to edit and patch.
        if (json.data && json.missing.every((m) => m.field !== "name" && m.field !== "input" && m.field !== "system")) {
          setData(json.data);
          setStage("edit");
        }
      } else {
        setMissing([{ field: "system", why: "Unexpected response from the parser. Please try again." }]);
      }
    } catch (err) {
      setMissing([{ field: "system", why: `Network error — ${String((err as Error).message ?? err)}` }]);
    } finally {
      setParsing(false);
    }
  };

  // Photo-of-form upload path. Staff drops a few snapshots of a
  // handwritten work-history form (or any document); we send them up
  // as data URLs to the server, which OCRs them with a vision LLM and
  // runs the same parser used by the manual-paste flow. Auto-advances
  // to the edit stage with everything pre-filled when extraction
  // succeeds — staff just confirms.
  const handleExtractFromImages = async (images: string[]): Promise<{ ok: boolean; error?: string }> => {
    setParsing(true);
    setMissing([]);
    try {
      const res = await fetch("/api/resume/extract-from-images", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ jobId, images }),
      });
      const json = (await res.json()) as ParseResponse & { text?: string; error?: string };
      // Always populate the textarea with the extracted text so staff
      // can see what was read off the paper, even if the parser bailed.
      if (typeof json.text === "string" && json.text.length > 0) {
        setPaste(json.text);
      }
      if (json.ok && json.data) {
        setData(json.data);
        setStage("edit");
        return { ok: true };
      }
      if (json.missing && json.missing.length > 0) {
        setMissing(json.missing);
        if (
          json.data &&
          json.missing.every((m) => m.field !== "name" && m.field !== "input" && m.field !== "system")
        ) {
          setData(json.data);
          setStage("edit");
          return { ok: true };
        }
        return { ok: false };
      }
      const errMsg = json.error ?? "Unexpected response from the form reader.";
      setMissing([{ field: "system", why: errMsg }]);
      return { ok: false, error: errMsg };
    } catch (err) {
      const errMsg = `Upload failed — ${String((err as Error).message ?? err)}`;
      setMissing([{ field: "system", why: errMsg }]);
      return { ok: false, error: errMsg };
    } finally {
      setParsing(false);
    }
  };

  const handleDownload = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      const res = await fetch("/api/resume/render-pdf", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ data, template, paletteId }),
      });
      if (!res.ok) {
        const txt = await res.text();
        alert(`Render failed (${res.status}): ${txt}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.first}_${data.last}_resume.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resume builder"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(26, 19, 14, 0.45)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        className="dark-tile"
        style={{
          width: "94vw",
          height: "94vh",
          maxWidth: 1400,
          background: "#16140e",
          color: "#f4efe2",
          border: "none",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18), 0 24px 56px -12px rgba(0, 0, 0, 0.35)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          stage={stage}
          template={template}
          paletteId={paletteId}
          onTemplate={(t) => {
            setTemplate(t);
            setPaletteId(t === "courier" ? "sage" : "lavender");
          }}
          onPalette={setPaletteId}
          onClose={onClose}
        />

        {stage === "paste" ? (
          <PasteStage
            paste={paste}
            setPaste={setPaste}
            missing={missing}
            parsing={parsing}
            onParse={handleParse}
            onExtractFromImages={handleExtractFromImages}
          />
        ) : (
          data && (
            <EditStage
              data={data}
              setData={(updater) =>
                setData((prev) => (prev ? updater(prev) : prev))
              }
              template={template}
              paletteId={paletteId}
              onBack={() => setStage("paste")}
              onDownload={handleDownload}
              downloading={downloading}
            />
          )
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────

function Header({
  stage,
  template,
  paletteId,
  onTemplate,
  onPalette,
  onClose,
}: {
  stage: Stage;
  template: Template;
  paletteId: string;
  onTemplate: (t: Template) => void;
  onPalette: (id: string) => void;
  onClose: () => void;
}) {
  const palettes = template === "courier" ? COURIER_PALETTES : STATEMENT_PALETTES;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 24px",
        borderBottom: "1px solid rgba(244,239,226,0.12)",
        gap: 24,
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
        <span
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 24,
            letterSpacing: "-0.01em",
          }}
        >
          Resume Builder
        </span>
        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "rgba(244,239,226,0.5)",
            fontWeight: 600,
          }}
        >
          {stage === "paste" ? "Paste your background" : "Edit + preview"}
        </span>
      </div>

      {stage === "edit" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 24, flex: 1, justifyContent: "center" }}>
          {/* Template picker */}
          <div style={{ display: "flex", gap: 6 }}>
            {(["courier", "statement"] as Template[]).map((t) => (
              <button
                key={t}
                onClick={() => onTemplate(t)}
                style={{
                  padding: "6px 14px",
                  background: template === t ? "rgba(244,239,226,0.15)" : "transparent",
                  color: "#f4efe2",
                  border: "1px solid rgba(244,239,226,0.25)",
                  borderRadius: 100,
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t}
              </button>
            ))}
          </div>
          {/* Palette swatches */}
          <div style={{ display: "flex", gap: 8 }}>
            {palettes.map((p) => {
              const isActive = paletteId === p.id;
              const swatch = "railBg" in p ? p.railBg : p.pitchBgFrom;
              return (
                <button
                  key={p.id}
                  onClick={() => onPalette(p.id)}
                  title={p.name}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: swatch,
                    border: isActive
                      ? "2px solid #f4efe2"
                      : "2px solid rgba(244,239,226,0.25)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "rgba(244,239,226,0.08)",
          color: "#f4efe2",
          border: "1px solid rgba(244,239,226,0.18)",
          fontSize: 18,
          cursor: "pointer",
          fontFamily: "inherit",
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PasteStage
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// Image downscaling
//
// Forms come in as 4-12MB photos straight off a phone. Vision OCR doesn't
// need 4K — 2000px on the long edge is plenty for handwriting recognition.
// We re-encode as JPEG at 80% quality on the client so total upload
// payload stays under the 12MB JSON limit even with 6+ pages.

async function downscaleToDataUrl(file: File, maxEdge = 2000, quality = 0.82): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("could not read image, file may be corrupt"));
      el.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("image has zero dimensions");
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas context unavailable");
    ctx.drawImage(img, 0, 0, nw, nh);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface UploadedPage {
  id: string;
  name: string;
  dataUrl: string;
}

function PasteStage({
  paste,
  setPaste,
  missing,
  parsing,
  onParse,
  onExtractFromImages,
}: {
  paste: string;
  setPaste: (v: string) => void;
  missing: MissingField[];
  parsing: boolean;
  onParse: () => void;
  onExtractFromImages: (images: string[]) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [pages, setPages] = useState<UploadedPage[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const addFiles = async (files: FileList | File[]) => {
    setUploadError(null);
    const incoming = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (incoming.length === 0) {
      setUploadError("Pick image files (.jpg or .png), those are what we can read.");
      return;
    }
    const next: UploadedPage[] = [];
    for (const f of incoming) {
      try {
        const dataUrl = await downscaleToDataUrl(f);
        next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: f.name || "image", dataUrl });
      } catch (err) {
        setUploadError(`Couldn't read ${f.name || "image"} — ${String((err as Error).message ?? err)}`);
      }
    }
    if (next.length > 0) setPages((prev) => [...prev, ...next].slice(0, 12));
  };

  const removePage = (id: string) => setPages((prev) => prev.filter((p) => p.id !== id));
  const clearPages = () => setPages([]);

  const triggerFilePicker = () => fileInputRef.current?.click();
  const triggerCamera = () => cameraInputRef.current?.click();

  const onExtract = async () => {
    if (pages.length === 0 || extracting || parsing) return;
    setExtracting(true);
    setUploadError(null);
    const result = await onExtractFromImages(pages.map((p) => p.dataUrl));
    if (!result.ok && result.error) setUploadError(result.error);
    setExtracting(false);
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) void addFiles(e.dataTransfer.files);
  };

  const busy = extracting || parsing;
  return (
    <div
      style={{
        flex: 1,
        padding: "32px 60px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        overflow: "auto",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto", width: "100%" }}>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: 36,
            margin: "0 0 12px 0",
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
        >
          Paste everything you can about your background.
        </h2>
        <p
          style={{
            color: "rgba(244,239,226,0.7)",
            fontSize: 14,
            lineHeight: 1.6,
            marginBottom: 22,
          }}
        >
          Work history, education, certifications, volunteer roles, skills,
          contact info, all in one block. Don'
          system will pull it apart and ask for anything missing. We already
          have your name on file, and your address if it's recorded.
        </p>

        {missing.length > 0 ? (
          <div
            style={{
              padding: "16px 20px",
              borderRadius: 8,
              background: "rgba(176, 57, 57, 0.18)",
              border: "1px solid rgba(176, 57, 57, 0.45)",
              marginBottom: 18,
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: "#e8a59f",
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              Missing, please add to your paste below
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: "#f4efe2" }}>
              {missing.map((m, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 13,
                    lineHeight: 1.55,
                    margin: "5px 0",
                  }}
                >
                  {m.why}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* ── Upload form photos ─────────────────────────────────────── */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!dragActive) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          style={{
            border: `1.5px dashed ${dragActive ? "#cbb87a" : "rgba(244,239,226,0.22)"}`,
            background: dragActive ? "rgba(203,184,122,0.08)" : "rgba(244,239,226,0.025)",
            borderRadius: 12,
            padding: "20px 22px",
            marginBottom: 22,
            transition: "background 160ms ease, border-color 160ms ease",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", color: "#cbb87a", fontWeight: 700, marginBottom: 8 }}>
                Have a paper form?
              </div>
              <div style={{ fontSize: 14, color: "rgba(244,239,226,0.8)", lineHeight: 1.55 }}>
                Snap photos of each page or drop image files here, we'
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            <button
              type="button"
              onClick={triggerCamera}
              disabled={busy}
              style={uploadButtonStyle(busy)}
              title="Open camera (mobile) or file picker (desktop)"
            >
              📷 Take photo
            </button>
            <button
              type="button"
              onClick={triggerFilePicker}
              disabled={busy}
              style={uploadButtonStyle(busy)}
              title="Pick one or more image files"
            >
              📁 Choose files
            </button>
            {pages.length > 0 && (
              <button
                type="button"
                onClick={clearPages}
                disabled={busy}
                style={{ ...uploadButtonStyle(busy), background: "transparent", color: "rgba(244,239,226,0.6)" }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Hidden inputs — desktop file picker + mobile camera */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (e.target.files?.length) void addFiles(e.target.files);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              if (e.target.files?.length) void addFiles(e.target.files);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />

          {pages.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(244,239,226,0.55)", fontWeight: 600, marginBottom: 10 }}>
                {pages.length} {pages.length === 1 ? "page" : "pages"} ready
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {pages.map((p, i) => (
                  <div
                    key={p.id}
                    style={{
                      position: "relative",
                      width: 88,
                      height: 110,
                      borderRadius: 6,
                      overflow: "hidden",
                      border: "1px solid rgba(244,239,226,0.18)",
                      background: "#0e0c08",
                    }}
                  >
                    <img
                      src={p.dataUrl}
                      alt={p.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: "4px 6px",
                        background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.7))",
                        fontSize: 10,
                        color: "#f4efe2",
                        fontFamily: "var(--font-mono, monospace)",
                        fontWeight: 600,
                      }}
                    >
                      {i + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => removePage(p.id)}
                      disabled={busy}
                      title="Remove"
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(0,0,0,0.7)",
                        color: "#f4efe2",
                        border: "1px solid rgba(244,239,226,0.3)",
                        cursor: busy ? "not-allowed" : "pointer",
                        fontSize: 12,
                        lineHeight: 1,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <button
                  type="button"
                  onClick={onExtract}
                  disabled={busy || pages.length === 0}
                  style={{
                    padding: "12px 24px",
                    background: busy ? "rgba(203,184,122,0.25)" : "#cbb87a",
                    color: busy ? "rgba(244,239,226,0.5)" : "#16140e",
                    border: "none",
                    borderRadius: 100,
                    fontSize: 12,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    cursor: busy ? "wait" : "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {extracting ? "Reading pages…" : parsing ? "Parsing…" : `Extract from ${pages.length} ${pages.length === 1 ? "page" : "pages"}`}
                </button>
              </div>
            </div>
          )}

          {uploadError && (
            <div style={{ marginTop: 12, fontSize: 13, color: "#e8a59f" }}>
              {uploadError}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            margin: "8px 0 18px 0",
          }}
        >
          <div style={{ flex: 1, height: 1, background: "rgba(244,239,226,0.12)" }} />
          <div style={{ fontSize: 10, letterSpacing: "0.4em", textTransform: "uppercase", color: "rgba(244,239,226,0.45)", fontWeight: 600 }}>
            Or paste below
          </div>
          <div style={{ flex: 1, height: 1, background: "rgba(244,239,226,0.12)" }} />
        </div>

        <textarea
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder={`

I'm Marcus Whitfield, 42, Newark NJ. Phone (973) 555-0172, email marcus.j.whitfield@gmail.com.

I've been a peer recovery specialist at Hudson County Community of Hope since March 2022. Before that I volunteered as an intake coordinator at New Hope Reentry Center in Newark from January 2020 through February 2022. Before THAT I led a 5-person framing crew at Pathways Building Co-op in Trenton from July 2018 to December 2019.

I have an A.A.S. in Human Services from Hudson County Community College (2021, Dean's List). I'm a CCAR-certified Recovery Coach (2019, certification #RCA-19-0418). I graduated East Side High School Newark in 2001.

I'm OSHA-10 certified, fluent in Spanish, trained in motivational interviewing and crisis de-escalation. I've trained 8 new peer specialists. I built a 24-hour warm-line that took 1,400+ crisis calls in year one. I'm looking for a coordinator role at a community-based nonprofit.`}
          style={{
            width: "100%",
            minHeight: 320,
            background: "#0e0c08",
            color: "#f4efe2",
            border: "1px solid rgba(244,239,226,0.18)",
            borderRadius: 8,
            padding: "14px 16px",
            fontSize: 14,
            lineHeight: 1.55,
            fontFamily: "'Source Serif 4', 'Inter', serif",
            resize: "vertical",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 16,
            gap: 12,
          }}
        >
          <button
            onClick={onParse}
            disabled={parsing || paste.trim().length < 30}
            style={{
              padding: "12px 28px",
              background: parsing ? "rgba(244,239,226,0.15)" : "#f4efe2",
              color: parsing ? "rgba(244,239,226,0.5)" : "#16140e",
              border: "none",
              borderRadius: 100,
              fontSize: 12,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              fontWeight: 700,
              cursor: parsing ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              opacity: paste.trim().length < 30 ? 0.4 : 1,
            }}
          >
            {parsing ? "Parsing…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function uploadButtonStyle(busy: boolean): CSSProperties {
  return {
    padding: "9px 16px",
    background: busy ? "rgba(244,239,226,0.06)" : "rgba(244,239,226,0.08)",
    color: busy ? "rgba(244,239,226,0.4)" : "#f4efe2",
    border: "1px solid rgba(244,239,226,0.22)",
    borderRadius: 100,
    fontSize: 12,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: busy ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// EditStage
// ─────────────────────────────────────────────────────────────────────────

function EditStage({
  data,
  setData,
  template,
  paletteId,
  onBack,
  onDownload,
  downloading,
}: {
  data: ResumeData;
  setData: (updater: (d: ResumeData) => ResumeData) => void;
  template: Template;
  paletteId: string;
  onBack: () => void;
  onDownload: () => void;
  downloading: boolean;
}) {
  // All mutators use the FUNCTIONAL form of setState — the updater receives
  // the latest data and returns a new object. This eliminates a class of
  // stale-closure bugs that hit specifically when the user types fast or
  // edits + deletes back-to-back: with the spread-current-data approach,
  // back-to-back updates within a single React batch all read the same
  // captured `data`, and only the last setState wins. The functional form
  // serializes correctly through React's batching.

  const update = (patch: Partial<ResumeData>) =>
    setData((prev) => ({ ...prev, ...patch }));

  const updateJob = (i: number, patch: Partial<ResumeJob>) =>
    setData((prev) => {
      const next = [...prev.experience];
      next[i] = { ...next[i]!, ...patch };
      return { ...prev, experience: next };
    });

  const updateBullet = (jobI: number, bI: number, value: string) =>
    setData((prev) => {
      const next = [...prev.experience];
      const job = { ...next[jobI]! };
      const bullets = [...job.bullets];
      const old = bullets[bI];
      // Editing an AI-suggested bullet KEEPS the ai flag — the user
      // accepted it as their starting point but the original credit is
      // still the model. They can clear it themselves with the "✓ accept"
      // button if they want it visually clean.
      bullets[bI] = old ? { ...old, text: value } : { text: value };
      job.bullets = bullets;
      next[jobI] = job;
      return { ...prev, experience: next };
    });

  /** Clear the ai flag on a bullet — used by the small "✓ accept" button
   *  next to AI-suggested bullets so the user can mark them as their
   *  own. The text stays the same. */
  const acceptBullet = (jobI: number, bI: number) =>
    setData((prev) => {
      const next = [...prev.experience];
      const job = { ...next[jobI]! };
      const bullets = [...job.bullets];
      const cur = bullets[bI];
      if (!cur) return prev;
      bullets[bI] = { text: cur.text }; // drop ai flag
      job.bullets = bullets;
      next[jobI] = job;
      return { ...prev, experience: next };
    });

  /** Polish a single bullet via the server LLM. Replaces the bullet's
   *  text with the rewritten version on success. Failure leaves the
   *  text unchanged (server returns ok:false but echoes the original). */
  const polishBullet = async (jobI: number, bI: number) => {
    const cur = data.experience[jobI]?.bullets[bI];
    if (!cur || !cur.text.trim()) return;
    const job = data.experience[jobI];
    try {
      const res = await fetch("/api/resume/polish", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          text: cur.text,
          role: job?.role,
          org: job?.org,
        }),
      });
      if (!res.ok) return;
      const { polished } = (await res.json()) as { polished: string };
      if (typeof polished !== "string" || !polished.trim()) return;
      setData((prev) => {
        const next = [...prev.experience];
        const j = { ...next[jobI]! };
        const bs = [...j.bullets];
        const old = bs[bI];
        if (!old) return prev;
        bs[bI] = { ...old, text: polished };
        j.bullets = bs;
        next[jobI] = j;
        return { ...prev, experience: next };
      });
    } catch {
      // Silent failure — leave the bullet alone.
    }
  };

  const removeJob = (i: number) =>
    setData((prev) => ({
      ...prev,
      experience: prev.experience.filter((_, k) => k !== i),
    }));

  const addJob = () =>
    setData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        {
          role: "",
          org: "",
          city: "",
          start: "",
          end: "",
          bullets: [{ text: "" }],
        },
      ],
    }));

  const addBullet = (jobI: number) =>
    setData((prev) => {
      const next = [...prev.experience];
      next[jobI] = {
        ...next[jobI]!,
        bullets: [...next[jobI]!.bullets, { text: "" }],
      };
      return { ...prev, experience: next };
    });

  const removeBullet = (jobI: number, bI: number) =>
    setData((prev) => {
      const next = [...prev.experience];
      next[jobI] = {
        ...next[jobI]!,
        bullets: next[jobI]!.bullets.filter((_, k) => k !== bI),
      };
      return { ...prev, experience: next };
    });

  const updateEdu = (i: number, patch: Partial<ResumeEducation>) =>
    setData((prev) => {
      const next = [...prev.education];
      next[i] = { ...next[i]!, ...patch };
      return { ...prev, education: next };
    });

  const removeEdu = (i: number) =>
    setData((prev) => ({
      ...prev,
      education: prev.education.filter((_, k) => k !== i),
    }));

  const addEdu = () =>
    setData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        { school: "", degree: "", year: "", detail: "" },
      ],
    }));

  const updateSkill = (i: number, value: string) =>
    setData((prev) => {
      const next = [...prev.skills];
      next[i] = value;
      return { ...prev, skills: next };
    });

  const removeSkill = (i: number) =>
    setData((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, k) => k !== i),
    }));

  const addSkill = () =>
    setData((prev) => ({ ...prev, skills: [...prev.skills, ""] }));

  const palette = useMemo(() => {
    if (template === "courier")
      return COURIER_PALETTES.find((p) => p.id === paletteId) ?? COURIER_PALETTES[0]!;
    return STATEMENT_PALETTES.find((p) => p.id === paletteId) ?? STATEMENT_PALETTES[0]!;
  }, [template, paletteId]);

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
      {/* LEFT — editor */}
      <div
        style={{
          width: "44%",
          minWidth: 460,
          padding: "24px 28px 20px 28px",
          overflow: "auto",
          borderRight: "1px solid rgba(244,239,226,0.12)",
        }}
      >
        <SectionTitle>Identity</SectionTitle>
        <Row>
          <Field
            label="First"
            value={data.first}
            onChange={(v) =>
              setData((prev) => ({
                ...prev,
                first: v,
                initials: computeInitials(v, prev.middle, prev.last),
              }))
            }
          />
          <Field
            label="Middle"
            value={data.middle ?? ""}
            onChange={(v) =>
              setData((prev) => ({
                ...prev,
                middle: v,
                initials: computeInitials(prev.first, v, prev.last),
              }))
            }
            optional
          />
          <Field
            label="Last"
            value={data.last}
            onChange={(v) =>
              setData((prev) => ({
                ...prev,
                last: v,
                initials: computeInitials(prev.first, prev.middle, v),
              }))
            }
          />
        </Row>
        <Row>
          <Field label="Title" value={data.title} onChange={(v) => update({ title: v })} flex={2} />
          <Field label="City" value={data.city} onChange={(v) => update({ city: v })} />
        </Row>
        <Row>
          <Field label="Email" value={data.email} onChange={(v) => update({ email: v })} flex={2} />
          <Field label="Phone" value={data.phone} onChange={(v) => update({ phone: v })} />
        </Row>

        <SectionTitle>Pitch / what you do</SectionTitle>
        <TextArea
          value={data.long_pitch}
          onChange={(v) => update({ long_pitch: v })}
          rows={3}
        />

        <SectionTitle>
          Experience
          <Density d={data.density} onChange={(d) => update({ density: d })} />
        </SectionTitle>
        {data.experience.map((j, i) => (
          <div
            key={i}
            style={{
              padding: "12px 0",
              borderTop: "1px solid rgba(244,239,226,0.08)",
            }}
          >
            <Row>
              <Field label="Role" value={j.role} onChange={(v) => updateJob(i, { role: v })} flex={2} />
              <RemoveButton onClick={() => removeJob(i)} />
            </Row>
            <Row>
              <Field label="Org" value={j.org} onChange={(v) => updateJob(i, { org: v })} flex={2} />
              <Field label="City" value={j.city} onChange={(v) => updateJob(i, { city: v })} />
            </Row>
            <Row>
              <Field label="Start" value={j.start} onChange={(v) => updateJob(i, { start: v })} />
              <Field label="End" value={j.end} onChange={(v) => updateJob(i, { end: v })} />
            </Row>
            <Label>Bullets</Label>
            {j.bullets.map((b, bi) => (
              <BulletRow
                key={bi}
                bullet={b}
                onChange={(v) => updateBullet(i, bi, v)}
                onAccept={() => acceptBullet(i, bi)}
                onPolish={() => polishBullet(i, bi)}
                onRemove={() => removeBullet(i, bi)}
              />
            ))}
            <AddButton onClick={() => addBullet(i)} label="+ Add bullet" />
          </div>
        ))}
        <AddButton onClick={addJob} label="+ Add job" big />

        <SectionTitle>Education</SectionTitle>
        {data.education.map((e, i) => (
          <div
            key={i}
            style={{
              padding: "10px 0",
              borderTop: "1px solid rgba(244,239,226,0.08)",
            }}
          >
            <Row>
              <Field label="School" value={e.school} onChange={(v) => updateEdu(i, { school: v })} flex={2} />
              <Field label="Year" value={e.year} onChange={(v) => updateEdu(i, { year: v })} />
              <RemoveButton onClick={() => removeEdu(i)} />
            </Row>
            <Field
              label="Degree"
              value={e.degree}
              onChange={(v) => updateEdu(i, { degree: v })}
              flex={3}
            />
          </div>
        ))}
        <AddButton onClick={addEdu} label="+ Add education" big />

        <SectionTitle>Skills</SectionTitle>
        {data.skills.map((s, i) => (
          <Row key={i}>
            <Field label="" value={s} onChange={(v) => updateSkill(i, v)} flex={3} />
            <RemoveButton onClick={() => removeSkill(i)} small />
          </Row>
        ))}
        <AddButton onClick={addSkill} label="+ Add skill" big />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 24,
            marginTop: 24,
            borderTop: "1px solid rgba(244,239,226,0.18)",
          }}
        >
          <button
            onClick={onBack}
            style={{
              fontSize: 11,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: "rgba(244,239,226,0.7)",
              background: "transparent",
              border: "1px solid rgba(244,239,226,0.25)",
              padding: "10px 16px",
              borderRadius: 100,
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            ← Edit paste
          </button>
          <button
            onClick={onDownload}
            disabled={downloading}
            style={{
              padding: "12px 28px",
              background: downloading ? "rgba(244,239,226,0.15)" : "#f4efe2",
              color: downloading ? "rgba(244,239,226,0.5)" : "#16140e",
              border: "none",
              borderRadius: 100,
              fontSize: 12,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              fontWeight: 700,
              cursor: downloading ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {downloading ? "Rendering…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* RIGHT — preview */}
      <div
        style={{
          flex: 1,
          background: "#0e0c08",
          padding: "24px",
          overflow: "auto",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <PreviewSheet>
          {template === "courier" ? (
            <CourierResume data={data} palette={palette as typeof COURIER_PALETTES[number]} />
          ) : (
            <StatementResume data={data} palette={palette as typeof STATEMENT_PALETTES[number]} />
          )}
        </PreviewSheet>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Editor primitives
// ─────────────────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 22,
        marginBottom: 10,
        fontSize: 10.5,
        letterSpacing: "0.36em",
        textTransform: "uppercase",
        color: "rgba(244,239,226,0.55)",
        fontWeight: 700,
        paddingBottom: 6,
        borderBottom: "1px solid rgba(244,239,226,0.15)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-end",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 9,
        letterSpacing: "0.32em",
        textTransform: "uppercase",
        color: "rgba(244,239,226,0.4)",
        fontWeight: 700,
        marginTop: 6,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  flex = 1,
  optional = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  flex?: number;
  optional?: boolean;
}) {
  return (
    <div style={{ flex, minWidth: 0 }}>
      {label ? (
        <div
          style={{
            fontSize: 9,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "rgba(244,239,226,0.4)",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          {label}
          {optional ? (
            <span style={{ marginLeft: 6, fontWeight: 400, opacity: 0.6 }}>opt.</span>
          ) : null}
        </div>
      ) : null}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          background: "#0e0c08",
          color: "#f4efe2",
          border: "1px solid rgba(244,239,226,0.18)",
          borderRadius: 4,
          padding: "8px 10px",
          fontSize: 13,
          fontFamily: "inherit",
        }}
      />
    </div>
  );
}

function TextArea({
  value,
  onChange,
  rows = 3,
  inline = false,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  inline?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      style={{
        flex: 1,
        width: inline ? undefined : "100%",
        background: "#0e0c08",
        color: "#f4efe2",
        border: "1px solid rgba(244,239,226,0.18)",
        borderRadius: 4,
        padding: "8px 10px",
        fontSize: 13,
        fontFamily: "'Source Serif 4', 'Inter', serif",
        lineHeight: 1.55,
        resize: "vertical",
        minWidth: 0,
      }}
    />
  );
}

function AddButton({
  onClick,
  label,
  big = false,
}: {
  onClick: () => void;
  label: string;
  big?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: big ? 12 : 6,
        background: "transparent",
        color: "rgba(244,239,226,0.7)",
        border: "1px dashed rgba(244,239,226,0.3)",
        borderRadius: 4,
        padding: big ? "8px 14px" : "5px 10px",
        fontSize: 11,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

/** A single bullet row in the editor. AI-suggested bullets get a subtle
 *  amber left border + tag; the polish button (✨) is always available so
 *  the user can rewrite a rough bullet they typed themselves. */
function BulletRow({
  bullet,
  onChange,
  onAccept,
  onPolish,
  onRemove,
}: {
  bullet: ResumeBullet;
  onChange: (v: string) => void;
  onAccept: () => void;
  onPolish: () => void | Promise<void>;
  onRemove: () => void;
}) {
  const [polishing, setPolishing] = useState(false);
  const isAi = bullet.ai === true;
  const handlePolish = async () => {
    if (polishing) return;
    setPolishing(true);
    try {
      await onPolish();
    } finally {
      setPolishing(false);
    }
  };
  return (
    <div
      style={{
        marginBottom: 8,
        position: "relative",
        padding: isAi ? "8px 10px" : 0,
        background: isAi ? "color-mix(in oklab, #d6b75a 10%, transparent)" : "transparent",
        border: isAi ? "1px solid color-mix(in oklab, #d6b75a 35%, var(--color-line))" : "none",
        borderRadius: isAi ? 6 : 0,
      }}
    >
      {isAi ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 3,
          }}
        >
          <span
            style={{
              fontSize: 8.5,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "#d6b75a",
              fontWeight: 700,
            }}
          >
            ✨ Suggested, review &amp; edit
          </span>
          <button
            onClick={onAccept}
            style={{
              fontSize: 8.5,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(244,239,226,0.65)",
              background: "transparent",
              border: "1px solid rgba(244,239,226,0.25)",
              borderRadius: 100,
              padding: "2px 8px",
              cursor: "pointer",
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            ✓ Accept
          </button>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
        <textarea
          value={bullet.text}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          style={{
            flex: 1,
            minWidth: 0,
            background: isAi ? "rgba(214,183,90,0.06)" : "#0e0c08",
            color: isAi ? "rgba(244,239,226,0.95)" : "#f4efe2",
            border: `1px solid ${isAi ? "rgba(214,183,90,0.4)" : "rgba(244,239,226,0.18)"}`,
            borderRadius: 4,
            padding: "8px 10px",
            fontSize: 13,
            fontFamily: "'Source Serif 4', 'Inter', serif",
            lineHeight: 1.55,
            resize: "vertical",
            fontStyle: isAi ? "italic" : "normal",
          }}
        />
        <button
          onClick={handlePolish}
          disabled={polishing}
          aria-label="Polish bullet"
          title="Rewrite in resume language"
          style={{
            width: 28,
            height: 28,
            background: polishing ? "rgba(214,183,90,0.3)" : "rgba(244,239,226,0.06)",
            color: polishing ? "#d6b75a" : "rgba(244,239,226,0.7)",
            border: "1px solid rgba(244,239,226,0.18)",
            borderRadius: "50%",
            fontSize: 13,
            cursor: polishing ? "wait" : "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {polishing ? "…" : "✨"}
        </button>
        <button
          onClick={onRemove}
          aria-label="Remove bullet"
          style={{
            width: 24,
            height: 24,
            background: "transparent",
            color: "rgba(244,239,226,0.5)",
            border: "1px solid rgba(244,239,226,0.18)",
            borderRadius: "50%",
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function RemoveButton({
  onClick,
  small = false,
}: {
  onClick: () => void;
  small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label="Remove"
      style={{
        width: small ? 24 : 28,
        height: small ? 24 : 28,
        background: "transparent",
        color: "rgba(244,239,226,0.5)",
        border: "1px solid rgba(244,239,226,0.18)",
        borderRadius: "50%",
        fontSize: small ? 13 : 16,
        cursor: "pointer",
        fontFamily: "inherit",
        flexShrink: 0,
        marginBottom: small ? 0 : 4,
      }}
    >
      ×
    </button>
  );
}

function Density({
  d,
  onChange,
}: {
  d: ResumeData["density"];
  onChange: (d: NonNullable<ResumeData["density"]>) => void;
}) {
  const cur = d ?? "balanced";
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {(["compact", "balanced", "rich"] as const).map((k) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          style={{
            padding: "3px 8px",
            background: cur === k ? "rgba(244,239,226,0.18)" : "transparent",
            color: cur === k ? "#f4efe2" : "rgba(244,239,226,0.55)",
            border: "1px solid rgba(244,239,226,0.18)",
            borderRadius: 100,
            fontSize: 8.5,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Preview sheet — scales the 8.5x11 resume down to fit the right pane
// ─────────────────────────────────────────────────────────────────────────

function PreviewSheet({ children }: { children: React.ReactNode }) {
  // The preview content is scaled to whatever fits the container. Wrap it
  // in a fixed-size shell, transform: scale, and let the outer flex
  // center it.
  const ref = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.65);

  useEffect(() => {
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const parent = el.parentElement;
      if (!parent) return;
      const w = parent.clientWidth - 48; // padding budget
      const sheetWidth = 8.5 * 96;
      const next = Math.min(w / sheetWidth, 0.95);
      setScale(Math.max(0.45, next));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (ref.current?.parentElement) ro.observe(ref.current.parentElement);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ flexShrink: 0 }}>
      <div
        style={{
          width: 8.5 * 96,
          height: 11 * 96,
          background: "white",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 8px 20px -4px rgba(0,0,0,0.4)",
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          marginBottom: (11 * 96 * (scale - 1)),
        }}
      >
        {/* Children render at native 8.5×11; the scale visually fits them. */}
        <style>{`
          .sheet { position: absolute; inset: 0; }
        `}</style>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function computeInitials(first: string, middle: string | undefined, last: string): string {
  return (
    (first ? first[0]! : "") +
    (middle ? middle[0]! : "") +
    (last ? last[0]! : "")
  ).toUpperCase();
}
